import { useEffect, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { useDebounce, useLanguage } from '../../../hooks';
import { useNestedLocationStore } from '../../../newStore/nestedLocationStore';
import { NestedLocation } from '../../../types/nestedLocations';
import { Checkbox } from '../../uiComponents/ComponentLibrary/Checkbox';
import {
  buildPreloadedLocationSelectorData,
  getFlattenedLocations,
  getLocationAncestors,
} from '../utils/LocationsHelpers';
import { LocationSelectorEmptyState } from './LocationSelectorEmptyState';
import { LocationSelectorRow } from './LocationSelectorRow';
import { SelectedLocations } from './LocationsSelectorReducer';

const ROW_HEIGHT = 60; // Approximate height of each row in pixels

interface LocationSelectorContentProps {
  selectedLocations: SelectedLocations;
  searchQuery: string;
  onSelect: (locationId: number) => void;
  allowedLocations?: NestedLocation[];
  showAltEmptyState?: boolean;
  multipleSelection?: boolean;
  allLocationsSelected?: boolean;
  onSelectAll?: () => void;
  locationsBlacklist?: number[];
  autoSelectChildren?: boolean;
  maxSelectableDepth?: number;
}

export function LocationSelectorContent({
  selectedLocations,
  searchQuery,
  onSelect,
  allowedLocations,
  showAltEmptyState = true,
  multipleSelection = true,
  allLocationsSelected = false,
  onSelectAll,
  autoSelectChildren = true,
  locationsBlacklist,
  maxSelectableDepth,
}: LocationSelectorContentProps) {
  const lang = useLanguage();
  const { locationsIndex: nestedLocationIndex } = useNestedLocationStore();
  const [expandedLocations, setExpandedLocations] = useState<Record<number, boolean>>({});

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const debouncedSetQuery = useDebounce(() => setDebouncedSearchQuery(searchQuery), 200);

  useEffect(() => {
    debouncedSetQuery();
  }, [searchQuery]);

  const locationsIndex = useMemo(() => {
    return buildPreloadedLocationSelectorData(
      nestedLocationIndex.locationById,
      debouncedSearchQuery,
      allowedLocations,
    );
  }, [allowedLocations, nestedLocationIndex, debouncedSearchQuery]);

  const handleToggleExpand = (locationId: number) => {
    setExpandedLocations((prev) => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  const rootLocations = Array.from(locationsIndex.locationById.values()).filter(
    (location) => location.parent_id === null,
  );

  const selectedLocationAncestors = useMemo(() => {
    if (!multipleSelection) {
      const selectedLocationId = selectedLocations.keys().next().value;
      if (!selectedLocationId) return [];
      return getLocationAncestors(selectedLocationId, locationsIndex.locationById).map(
        (ancestor) => ancestor.id,
      );
    }
    return [];
  }, [selectedLocations, locationsIndex, multipleSelection]);

  // Flatten the nested locations into a flat array for virtualization
  const flattenedLocations = useMemo(() => {
    const flattened = getFlattenedLocations({
      rootLocations,
      searchQuery: debouncedSearchQuery,
      selectedLocations,
      expandedLocations,
      locationsIndex,
      allLocationsSelected,
      multipleSelection,
      autoSelectChildren,
      selectedLocationAncestors,
      locationsBlacklist,
    });

    // Apply maxSelectableDepth constraint if provided
    // If selecting a location at level L as parent, the child will be at level L + 1
    // So disable locations where level + 1 >= maxSelectableDepth
    if (maxSelectableDepth !== undefined && maxSelectableDepth > 0) {
      return flattened.map((item) => {
        const exceedsMaxDepth = item.level + 1 >= maxSelectableDepth;
        const isDisabled = exceedsMaxDepth;
        if (isDisabled) {
          return {
            ...item,
            location: {
              ...item.location,
              selectable: false,
            },
          };
        }
        return item;
      });
    }

    return flattened;
  }, [
    rootLocations,
    debouncedSearchQuery,
    selectedLocations,
    expandedLocations,
    locationsIndex,
    allLocationsSelected,
    multipleSelection,
    autoSelectChildren,
    selectedLocationAncestors,
    locationsBlacklist,
    maxSelectableDepth,
  ]);

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: flattenedLocations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const hasLocationsCreated = nestedLocationIndex.locationById.size > 0;
  const isSearchWithNoResults = hasLocationsCreated && debouncedSearchQuery.trim() !== '';

  if (locationsIndex.locationById.size === 0) {
    return (
      <LocationSelectorEmptyState
        variant={isSearchWithNoResults ? 'searchWithNoResults' : 'noLocationsCreated'}
        showAltEmptyState={showAltEmptyState}
      />
    );
  }

  return (
    <div className='flex flex-col flex-1'>
      {onSelectAll && (
        <div className='flex items-center justify-between gap-0.5 mb-3'>
          <h2 className='font-medium text-neutral-10'>{lang.form_builder.locations}</h2>
          <div>
            <Checkbox
              checked={allLocationsSelected}
              onChange={onSelectAll}
            />
            <span className='text-sm'>{lang.form_builder.selectAll}</span>
          </div>
        </div>
      )}
      <div
        ref={parentRef}
        className='flex-1 overflow-auto md:max-h-[50vh] mb-2'
      >
        <div
          className='relative w-full'
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = flattenedLocations[virtualItem.index];
            const nextItem = flattenedLocations[virtualItem.index + 1] ?? null;

            return (
              <LocationSelectorRow
                key={virtualItem.key}
                virtualItem={virtualItem}
                item={item}
                multipleSelection={multipleSelection}
                nextItem={nextItem}
                virtualizer={virtualizer}
                onToggleExpand={handleToggleExpand}
                onSelect={(location) => onSelect(location.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
